import { createClient, FailedResponse } from '@citrite/http';
import type {
	SummarizableResourceType,
	SummarizationParams,
} from '@sharefiledev/qna-pilet-sdk';
import { logError } from 'logger';
import {
	FeedbackType,
	StatusRecord,
	Summary,
	SummaryResponse,
} from './contentqnaPluginTypes';
import { QnAContextMessage, QnACtx } from './qnactxPluginTypes';

export interface ContentQnAClient {
	/**
	 * Kick off the generation of a summary for a given resource
	 * @param params - summarization parameters
	 * @param isRetry - whether this is a retry of a previous summarization attempt
	 * @param ac - AbortController instance to allow cancelling the request
	 * @throws @see {FailedResponse} if the http request fails (server returns 4xx or 5xx)
	 */
	createSummary<T extends SummarizableResourceType>(
		params: SummarizationParams<T>,
		isRetry: boolean,
		ac: AbortController
	): Promise<SummaryResponse>;
	/**
	 * Get the status of a summarization task
	 * @param task_id - the task ID to check the status of
	 * @param ac - AbortController instance to allow cancelling the request
	 * @throws @see {FailedResponse} if the http request fails (server returns 4xx or 5xx)
	 */
	getSummaryTaskStatus(task_id: string, ac: AbortController): Promise<StatusRecord>;
	/**
	 * Get a summary resource by its Resource ID (RID)
	 * @param rid - the Resource ID of the summary to retrieve
	 * @param ac - AbortController instance to allow cancelling the request
	 * @throws @see {FailedResponse} if the http request fails (server returns 4xx or 5xx)
	 */
	getSummaryByRID(rid: string, ac: AbortController): Promise<Summary>;
	/**
	 * Report feedback on a summary
	 * @param rid - the Resource ID (RID) of the summary to report feedback on
	 * @param type - the type of feedback to report
	 * @param ac - AbortController instance to allow cancelling the request
	 * @throws @see {FailedResponse} if the http request fails (server returns 4xx or 5xx)
	 */
	reportSummaryFeedback(
		rid: string,
		type: FeedbackType,
		ac: AbortController
	): Promise<void>;
	/**
	 * Create a QnA request for a given source resource
	 * @param sourceResourceRid - the source ResourceID (RID) to create the QnA request for
	 * @param request - the QnA request string
	 * @param ac - AbortController instance to allow cancelling the request
	 * @throws @see {FailedResponse} if the http request fails (server returns 4xx or 5xx)
	 */
	createQnARequest(
		sourceResourceRid: string,
		request: string,
		retry: boolean,
		ac: AbortController
	): Promise<QnAContextMessage>;
	/**
	 * Get the status of a QnA request
	 * @param task_id - the task ID to check the status of
	 * @param qnactxRID - the QnA context RID to check the status of
	 * @param ac - AbortController instance to allow cancelling the request
	 * @throws @see {FailedResponse} if the http request fails (server returns 4xx or 5xx)
	 */
	getQnARequestStatus(
		task_id: string,
		qnactxRID: string,
		ac: AbortController
	): Promise<QnAContextMessage>;
	/**
	 * Get the QnA context for a given source resource
	 * @param sourceResourceId - the source ResourceID (RID) to get the QnA context for
	 * @param ac - AbortController instance to allow cancelling the request
	 * @throws @see {FailedResponse} if the http request fails (server returns 4xx or 5xx)
	 */
	getQnaContextForResource(
		sourceResourceId: string,
		ac: AbortController
	): Promise<QnACtx>;
	/**
	 * Delete a QnA context
	 * @param contextRid - RID for the QnA context resource that needs to be deleted
	 * @param ac - AbortController instance to allow cancelling the request
	 * @throws @see {FailedResponse} if the http request fails (server returns 4xx or 5xx)
	 */
	deleteQnAContext(contextRid: string, ac: AbortController): Promise<void>;
	/**
	 * Reports user's feedback for a generated response
	 * @param rid - the Resource ID (RID) of the QnA context to report feedback on
	 * @param type - the type of feedback to report
	 * @param ac - AbortController instance to allow cancelling the request
	 * @param taskId - the task ID of the response to report feedback on
	 * @throws @see {FailedResponse} if the http request fails (server returns 4xx or 5xx)
	 */
	reportQnAFeedback(
		rid: string,
		type: FeedbackType,
		ac: AbortController,
		taskId?: string
	): Promise<void>;
}

export interface ContentQnAClientConfiguration {
	baseUrl: string;
}

export function initializeClient(
	config: ContentQnAClientConfiguration
): ContentQnAClient {
	const qnaServiceHttpClient = createClient({
		baseURL: config.baseUrl,
		timeout: 60000,
		headers: {
			'x-bff-csrf': 'true',
		},
	});

	const qnaCtxsApiUrlPrefix = 'api/v1/resources/qnactx';
	const summarysApiUrlPrefix = 'api/v1/resources/summary';

	return {
		async createSummary<T extends SummarizableResourceType>(
			params: SummarizationParams<T>,
			isRetry: boolean,
			ac: AbortController
		): Promise<SummaryResponse> {
			return apiProxy(async () => {
				return await qnaServiceHttpClient.post(summarysApiUrlPrefix, undefined, {
					params: {
						forResourceId: params.rid,
						isRetry,
					},
					signal: ac.signal,
				});
			}, 'createSummary');
		},
		async getSummaryTaskStatus(
			task_id: string,
			ac: AbortController
		): Promise<StatusRecord> {
			return apiProxy(async () => {
				return await qnaServiceHttpClient.get(
					`${summarysApiUrlPrefix}/tasks/${encodeURIComponent(task_id)}`,
					{
						signal: ac.signal,
					}
				);
			}, 'getStatus');
		},
		async getSummaryByRID(rid: string, ac: AbortController): Promise<Summary> {
			return apiProxy(async () => {
				return await qnaServiceHttpClient.get(
					`${summarysApiUrlPrefix}/${encodeURIComponent(rid)}`,
					{
						signal: ac.signal,
					}
				);
			}, 'getSummaryByRID');
		},
		async reportSummaryFeedback(
			rid: string,
			type: FeedbackType,
			ac: AbortController
		): Promise<void> {
			return apiProxy(async () => {
				return await qnaServiceHttpClient.post(
					`${summarysApiUrlPrefix}/feedback`,
					{
						forResourceRID: rid,
						type,
					},
					{
						signal: ac.signal,
					}
				);
			}, 'reportSummaryFeedback');
		},
		async createQnARequest(
			sourceResourceRid: string,
			request: string,
			retry: boolean,
			ac: AbortController
		): Promise<QnAContextMessage> {
			return apiProxy(async () => {
				return await qnaServiceHttpClient.post(
					`${qnaCtxsApiUrlPrefix}/requests`,
					{
						sourceRID: sourceResourceRid,
						request,
						retry,
					},
					{
						signal: ac.signal,
					}
				);
			}, 'createQnARequest');
		},
		async getQnARequestStatus(
			task_id: string,
			resourceId: string,
			ac: AbortController
		): Promise<QnAContextMessage> {
			return apiProxy(async () => {
				return await qnaServiceHttpClient.get(
					`${qnaCtxsApiUrlPrefix}/requests/${encodeURIComponent(
						task_id
					)}?resourceId=${encodeURIComponent(resourceId)}`,
					{
						signal: ac.signal,
					}
				);
			}, 'getQnARequestStatus');
		},
		async getQnaContextForResource(
			sourceResourceId: string,
			ac: AbortController
		): Promise<QnACtx> {
			return apiProxy(async () => {
				return await qnaServiceHttpClient.get(
					`${qnaCtxsApiUrlPrefix}?sourceResourceId=${encodeURIComponent(
						sourceResourceId
					)}`,
					{
						signal: ac.signal,
					}
				);
			}, 'getQnaContextForResource');
		},
		async deleteQnAContext(contextRid: string, ac: AbortController): Promise<void> {
			return apiProxy(async () => {
				return await qnaServiceHttpClient.post(
					`${qnaCtxsApiUrlPrefix}/${encodeURIComponent(contextRid)}/delete`,
					{
						signal: ac.signal,
					}
				);
			}, 'deleteQnAContext');
		},
		async reportQnAFeedback(
			rid: string,
			type: FeedbackType,
			ac: AbortController,
			taskId?: string
		): Promise<void> {
			return apiProxy(async () => {
				return await qnaServiceHttpClient.post(
					`${qnaCtxsApiUrlPrefix}/feedback`,
					{
						forResourceRID: rid,
						taskId,
						type,
					},
					{
						signal: ac.signal,
					}
				);
			}, 'reportQnAFeedback');
		},
	};
}

async function apiProxy<T>(
	proxyFn: () => Promise<T>,
	proxyLabel: string,
	errorStatusCodesToIgnoreLoggingFor?: number[]
): Promise<T> {
	let response: T;
	try {
		response = await proxyFn();
	} catch (error) {
		const failedResponse = error as FailedResponse;

		const shouldNotLogError =
			!!errorStatusCodesToIgnoreLoggingFor &&
			errorStatusCodesToIgnoreLoggingFor.includes(failedResponse.status);
		const shouldLogError = !shouldNotLogError;
		if (shouldLogError) {
			logError(failedResponse, {
				customMessage: `ContentQnA Plugin API call failure for ${proxyLabel}`,
			});
		}
		throw failedResponse;
	}

	return response;
}
